import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
} from "../../field-ui";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import type { ChoiceRow } from "../../../../lib/forms/fieldOptions";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";

interface MultipleChoiceInputProps extends FormFieldInputProps {
  label: string;
  labelMuted?: boolean;
  choiceRows: ChoiceRow[];
  validation?: (value: string) => boolean;
}

function serializeSelected(selected: string[]): string {
  return JSON.stringify(selected);
}

function parseSelected(value: string | undefined): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
}

const MultipleChoiceInput = forwardRef<FormFieldRef, MultipleChoiceInputProps>(
  (props, ref) => {
    const shellRef = useRef<HTMLDivElement>(null);
    const [selected, setSelected] = useState<string[]>(() =>
      parseSelected(props.initialValue),
    );
    const [validationState, setValidationState] = useState(true);

    const commitSelected = (next: string[]) => {
      setSelected(next);
      reportFieldValue(
        props.fieldName,
        serializeSelected(next),
        props.onValueChange,
      );
    };

    const toggleValue = (choiceValue: string) => {
      const next = selected.includes(choiceValue)
        ? selected.filter((item) => item !== choiceValue)
        : [...selected, choiceValue];
      commitSelected(next);
      setValidationState(true);
    };

    const clear = () => {
      commitSelected([]);
    };

    const validate = () => {
      if (!props.validation) return true;
      const result = props.validation(serializeSelected(selected));
      setValidationState(result);
      return result;
    };

    useImperativeHandle(ref, () => ({
      setValidationState,
      value: serializeSelected(selected),
      scrollToElement: (options) => {
        shellRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          ...options,
        });
      },
      fieldId: props.fieldId,
      fieldName: props.fieldName,
      validate,
      clear,
    }));

    return (
      <FieldShell ref={shellRef}>
        {props.label.trim() || props.labelMuted ? (
          <FieldLabel muted={props.labelMuted}>{props.label}</FieldLabel>
        ) : null}
        <FieldControl>
          <div className="space-y-2">
            {props.choiceRows.map((row) => {
              const checked = selected.includes(row.value);
              const inputId = `${props.fieldId}-${row.value}`;
              return (
                <label
                  key={row.value}
                  htmlFor={inputId}
                  className="flex cursor-pointer items-start gap-2 font-onest text-sm text-slate-700"
                >
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleValue(row.value)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
                  />
                  <span>{row.label}</span>
                </label>
              );
            })}
          </div>
        </FieldControl>
        {!validationState ? (
          <FieldFooter>Выберите хотя бы один вариант</FieldFooter>
        ) : null}
      </FieldShell>
    );
  },
);

MultipleChoiceInput.displayName = "MultipleChoiceInput";

export { MultipleChoiceInput };
